import { debounce, type DebouncedFunc } from "lodash";

type Waiting<R> = { resolve: (result: R) => void; reject: (error: unknown) => void };

type Pending<A, R> = { debounced: DebouncedFunc<(args: A) => void>; waiting: Waiting<R>[] };

export const debouncePerKey = <A, R = void>(
  fn: (args: A) => R | Promise<R>,
  keyOf: (args: A) => string,
  wait: number,
): ((args: A) => Promise<R>) => {
  const pending = new Map<string, Pending<A, R>>();
  const pendingFor = (key: string): Pending<A, R> => {
    const existing = pending.get(key);
    if (existing) return existing;
    const created: Pending<A, R> = {
      waiting: [],
      debounced: debounce((args: A) => {
        const settled = created.waiting.splice(0);
        new Promise<R>((resolve) => resolve(fn(args))).then(
          (result) => settled.forEach(({ resolve }) => resolve(result)),
          (error) => settled.forEach(({ reject }) => reject(error)),
        );
      }, wait),
    };
    pending.set(key, created);
    return created;
  };
  return (args: A) =>
    new Promise<R>((resolve, reject) => {
      const { debounced, waiting } = pendingFor(keyOf(args));
      waiting.push({ resolve, reject });
      debounced(args);
    });
};
